"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Snowflake, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const leftColumnVariants = {
    hidden: {
      opacity: 0,
      x: shouldReduceMotion ? 0 : -20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const rightColumnVariants = {
    hidden: {
      opacity: 0,
      x: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.1
      }
    }
  };
  const highlights = [{
    title: "Trailer Sizes",
    desc: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability and requirements are confirmed individually.",
    icon: Snowflake
  }, {
    title: "Service Area",
    desc: "Serving our listed Southern California and Las Vegas-area communities",
    icon: MapPin
  }, {
    title: "Delivery and Setup",
    desc: "Delivery, positioning, setup, and pickup arrangements are confirmed with each quote. Site-access and delivery requirements for the 53-foot reefer trailer are reviewed individually.",
    icon: ShieldCheck
  }];
  return <section id="our-story" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column: Company Story */}
          <motion.div variants={leftColumnVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col justify-center space-y-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/30 border border-border">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground font-sans">
                  Company Background
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-sans tracking-tight text-foreground leading-tight">
                About Fridge Fleet
              </h1>
            </div>

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p>Founded in 2026, Fridge Fleet is a family-owned and operated mobile refrigeration and cold-storage trailer rental company serving Southern California and the listed Las Vegas-area communities. Trailer options include 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer for potential large-capacity commercial needs. Availability, specifications, delivery requirements, intended use, and suitability are confirmed with each quote.</p>
              <div className="pt-2 space-y-2">
                <h2 className="text-xl sm:text-2xl font-bold font-sans tracking-tight text-foreground">
                  Why We Started Fridge Fleet
                </h2>
                <p>
                  Fridge Fleet was started to provide customers with a straightforward mobile refrigeration rental experience built around equipment options, clear communication, and careful coordination. We work with customers to review requested dates, delivery location, intended use, temperature requirements, trailer size, site access, available power, and optional-equipment needs from the initial quote through final pickup.
                </p>
              </div>
            </div>

            {/* Quick Spec Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {highlights.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.title} data-index={i} className="p-3.5 rounded-lg bg-card border border-border flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-muted text-foreground">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-semibold text-xs sm:text-sm text-foreground font-sans">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.title === "Trailer Sizes" ? <>
                          Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a{" "}
                          <Link to="/FootRefrigeratedTrailerRentalsPage" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                            53-foot reefer trailer
                          </Link>
                          . Availability and requirements are confirmed individually.
                        </> : item.desc}
                    </p>
                  </div>;
            })}
            </div>

            {/* Dual CTA dispatch cluster */}
            <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs">
                <Link to="/ContactRequestAQuotePage">
                  <span>Request a Quote</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <a href="tel:2135218821" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-muted text-muted-foreground text-sm font-medium border border-border">
                <span>213-521-8821</span>
              </a>
            </div>
          </motion.div>

          {/* Right Column: Featured Image with Chamfered Precision Frame */}
          <motion.div variants={rightColumnVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 relative">
            {/* Precision Chamfered Frame Container */}
            <div className="relative p-2 sm:p-3 bg-card border border-border rounded-lg shadow-md">
              {/* Corner Accent Ticks */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary rounded-tl-sm -mt-0.5 -ml-0.5 pointer-events-none" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-primary rounded-tr-sm -mt-0.5 -mr-0.5 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-primary rounded-bl-sm -mb-0.5 -ml-0.5 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-primary rounded-br-sm -mb-0.5 -mr-0.5 pointer-events-none" />

              <div className="relative overflow-hidden rounded-md aspect-4/3 sm:aspect-16/11 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_37-PM-1.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_37-PM-1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_46_37-PM-1.png-wvc-sizes" alt="Fridge Fleet mobile refrigeration trailer parked at a restaurant receiving dock." className="w-full h-full object-cover" loading="lazy" />
              </div>

              {/* Status Neutral Region Tag */}
              <div className="mt-3 px-3 py-2 bg-muted/60 rounded-md border border-border flex items-center justify-center text-xs text-muted-foreground font-medium">
                <span>Fridge Fleet Mobile Refrigeration Trailer</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}